import { useState } from "react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { api, formatApiErrorDetail } from "@/lib/api";
import { COMPANY } from "@/data/site";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Mail, Phone, MapPin, Loader2 } from "lucide-react";

const EMPTY = { name: "", email: "", phone: "", company: "", service: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Inquiry sent! Our team will get back to you shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send inquiry.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 lg:py-40 border-t border-border">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-20">
          {/* Left: info */}
          <div className="lg:col-span-5">
            <Reveal>
              <span className="font-mono text-xs tracking-[0.35em] text-primary uppercase">06 — Get In Touch</span>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display font-medium text-4xl lg:text-6xl tracking-tight leading-[1.02]">
                Let&apos;s build<br />something precise.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 text-muted-foreground text-base leading-relaxed max-w-md">
                Send us your drawings, volumes and timelines. Our engineering team will respond
                with a manufacturability review and quote.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-12 space-y-6">
                <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-4 group" data-testid="contact-email">
                  <span className="h-11 w-11 border border-border flex items-center justify-center group-hover:border-primary transition-colors">
                    <Mail className="w-4 h-4 text-primary" />
                  </span>
                  <span className="text-foreground/90 group-hover:text-primary transition-colors">{COMPANY.email}</span>
                </a>
                <a href={`tel:${COMPANY.phone}`} className="flex items-center gap-4 group" data-testid="contact-phone">
                  <span className="h-11 w-11 border border-border flex items-center justify-center group-hover:border-primary transition-colors">
                    <Phone className="w-4 h-4 text-primary" />
                  </span>
                  <span className="text-foreground/90 group-hover:text-primary transition-colors">{COMPANY.phone}</span>
                </a>
                <div className="flex items-start gap-4">
                  <span className="h-11 w-11 border border-border flex items-center justify-center shrink-0">
                    <MapPin className="w-4 h-4 text-primary" />
                  </span>
                  <span className="text-muted-foreground text-sm leading-relaxed max-w-xs">{COMPANY.address}</span>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <form onSubmit={submit} data-testid="contact-form" className="border border-border bg-card p-7 lg:p-10 space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="font-mono text-xs tracking-widest uppercase text-muted-foreground">Name *</Label>
                    <Input id="name" data-testid="input-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Your name" className="rounded-none bg-background border-border h-12" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="font-mono text-xs tracking-widest uppercase text-muted-foreground">Email *</Label>
                    <Input id="email" type="email" data-testid="input-email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@company.com" className="rounded-none bg-background border-border h-12" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="font-mono text-xs tracking-widest uppercase text-muted-foreground">Phone</Label>
                    <Input id="phone" data-testid="input-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="93868 19589" className="rounded-none bg-background border-border h-12" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company" className="font-mono text-xs tracking-widest uppercase text-muted-foreground">Company</Label>
                    <Input id="company" data-testid="input-company" value={form.company} onChange={(e) => update("company", e.target.value)} placeholder="Company name" className="rounded-none bg-background border-border h-12" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="font-mono text-xs tracking-widest uppercase text-muted-foreground">Service Interested In</Label>
                  <Select value={form.service} onValueChange={(v) => update("service", v)}>
                    <SelectTrigger data-testid="select-service" className="rounded-none bg-background border-border h-12">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {COMPANY.services.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`service-option-${s.replace(/[^a-zA-Z]/g, "")}`}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="font-mono text-xs tracking-widest uppercase text-muted-foreground">Message *</Label>
                  <Textarea id="message" data-testid="input-message" value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us about your project, volumes and timelines..." rows={5} className="rounded-none bg-background border-border resize-none" />
                </div>

                <Button type="submit" disabled={loading} data-testid="submit-inquiry" className="w-full rounded-none h-13 py-4 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-base">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : "Send Inquiry"}
                </Button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
